
// Import necessary types and client
import { environmentSupabase } from '@/integrations/supabase/environment-client';
import type { Poll, PollQuestions } from '@/types/poll';
import { transformSupabasePollToPoll } from '@/utils/polls/pollDataTransformers';
import { envLog } from '@/config/environment';

/**
 * Fetch polls linked to a specific insight card
 * @param cardId ID of the insight card
 * @param linkedPollId Optional specific poll ID to fetch
 * @returns Array of linked polls
 */
export const fetchCardLinkedPolls = async (
  cardId: string,
  linkedPollId?: string | null
): Promise<Poll[]> => {
  try {
    envLog.debug('Fetching linked polls for card:', cardId);

    let query = environmentSupabase
      .from('poll_card_links')
      .select('poll_id')
      .eq('card_id', cardId);

    if (linkedPollId) {
      query = query.eq('poll_id', linkedPollId);
    }

    const { data: links, error: linksError } = await query;

    if (linksError) {
      envLog.error('Error fetching poll links:', linksError);
      return [];
    }

    if (!links.length) {
      envLog.debug('No linked polls found for card:', cardId);
      return [];
    }

    // Extract poll IDs
    const pollIds = links.map(link => link.poll_id);
    envLog.debug('Found poll IDs:', pollIds);

    // Fetch the actual polls
    const { data: polls, error: pollsError } = await environmentSupabase
      .from('polls')
      .select('*')
      .in('id', pollIds);

    if (pollsError) {
      envLog.error('Error fetching polls:', pollsError);
      return [];
    }

    // Transform the database results to match the Poll type structure using the utility function
    const transformedPolls = polls.map(poll => transformSupabasePollToPoll(poll));
    envLog.debug('Successfully fetched and transformed polls:', transformedPolls.length);

    return transformedPolls;
  } catch (error) {
    envLog.error('Exception in fetchCardLinkedPolls:', error);
    return [];
  }
};

/**
 * Test database connectivity and log environment info
 */
export const testDatabaseConnection = async (): Promise<void> => {
  try {
    envLog.info('Testing database connection...');
    
    const { data, error } = await environmentSupabase
      .from('countries')
      .select('count')
      .limit(1);

    if (error) {
      envLog.error('Database connection test failed:', error);
    } else {
      envLog.info('Database connection test successful');
    }
  } catch (error) {
    envLog.error('Database connection test exception:', error);
  }
};
